import { Permission } from '_metronic/helpers'
import { useAuth } from '../../../../auth'

/**
 * Gates de UI da tela de acompanhamento de envios ao PEC e-SUS APS (E5d).
 *
 * LIMITAÇÃO CONHECIDA (arquitetura), a mesma já registrada em `unidades/.../_esusPermissao.ts` (E5a)
 * e `funcionarios/.../_lotacaoPermissao.ts` (E5b): o frontend não consegue checar a permissão pelo
 * nome. O endpoint `/usuarios/me` devolve `UsuarioDtoVinculo`, cujo `grupo` é montado por
 * `new GrupoDto(usuario.getGrupo())` — construtor que NÃO preenche a lista `perfis` (as permissões).
 * A única informação de autorização disponível no cliente é o NOME DO GRUPO
 * (`currentUser.grupo.nome`). O backend continua sendo a autoridade real: em qualquer divergência a
 * chamada volta 403.
 *
 * CONSEQUÊNCIA IMPORTANTE PARA QUEM FOR TESTAR: com o `allowedPermissions: [SuperAdmin, Admin]` da
 * rota `apps/esus/*`, **todo** grupo que consegue abrir esta tela já tem `Esus:read`, `Esus:resend`
 * e `Atendimento:corrigirLotacao` — os três listados abaixo. O estado "logado, dentro da tela, sem
 * permissão de escrita" é **inalcançável pela UI hoje**; estes hooks são profundidade defensiva
 * sobre o 403 real do backend, para o dia em que os grupos de cada permissão divergirem no
 * `permission.json`. Não replicar o padrão indefinidamente: o conserto de verdade é `/usuarios/me`
 * expor `grupo.perfis`.
 */

/** Grupos de `Esus:read` no seed do backend (`permission.json`). Protege listar/detalhar envios. */
export const GRUPOS_ESUS_READ: Permission[] = [Permission.SuperAdmin, Permission.Admin]

/**
 * Grupos de `Esus:resend` (`permission.json`). Protege `POST .../{id}/reenvio`.
 *
 * Hoje é **idêntico** a `GRUPOS_ESUS_READ`; mantido separado de propósito para não confundir gate de
 * leitura com gate de escrita se os dois divergirem no seed.
 */
export const GRUPOS_ESUS_RESEND: Permission[] = [Permission.SuperAdmin, Permission.Admin]

/** Grupos de `Atendimento:corrigirLotacao` (`permission.json`). Protege o PATCH de E1c. */
export const GRUPOS_CORRIGIR_LOTACAO: Permission[] = [
  Permission.SuperAdmin,
  Permission.SuperMedico,
  Permission.Admin,
  Permission.Medico,
]

export const MSG_SEM_PERMISSAO_ESUS_REENVIO =
  'Você não tem permissão para reenviar fichas ao e-SUS APS. ' +
  'Solicite ao administrador da instituição a permissão "Esus:resend".'

export const MSG_SEM_PERMISSAO_CORRIGIR_LOTACAO =
  'Você não tem permissão para corrigir a lotação do atendimento. ' +
  'Solicite ao administrador da instituição a permissão "Atendimento:corrigirLotacao".'

const useGrupoEm = (grupos: Permission[]): boolean => {
  const { currentUser } = useAuth()
  return grupos.includes(currentUser?.grupo?.nome as Permission)
}

/** Pode listar e detalhar envios (`Esus:read`). */
export const usePermissaoEsusLeitura = (): boolean => useGrupoEm(GRUPOS_ESUS_READ)

/** Pode disparar o reenvio manual (`Esus:resend`). */
export const usePermissaoEsusReenvio = (): boolean => useGrupoEm(GRUPOS_ESUS_RESEND)

/** Pode corrigir o carimbo de lotação do atendimento (`Atendimento:corrigirLotacao`). */
export const usePermissaoCorrigirLotacao = (): boolean => useGrupoEm(GRUPOS_CORRIGIR_LOTACAO)
